import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  Signal,
  ViewChild,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';

import { CourseService } from '../../../api/course/course.service';
import { CourseCatalogueSidebarComponent } from './course-catalogue-sidebar/course-catalogue-sidebar.component';
import { CollectionViewer } from '@angular/cdk/collections';
import { Course } from '../../../api/course/course.model';
import { FilterService } from '../../../util/filters/filter.service';
import { CourseCatalogueCardComponent } from './course-catalogue-card/course-catalogue-card.component';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  APP_FILTER_SERVICE_CONFIG,
  FilterServiceConfig,
} from '../../../util/filters/filter.interfaces';
import { FrontendHeaderComponent } from '../../_layout/frontend-header/frontend-header.component';
import { RouterLink } from '@angular/router';
import { CategoryCardSkeletonComponent } from '../../home/category-section/category-card-skeleton/category-card-skeleton.component';
import { accumulatingDataSource } from '../../../api/_lib/data-source/api-data-source';
import { map, skip, startWith } from 'rxjs';

@Component({
  selector: 'app-courses-overview',
  standalone: true,
  imports: [
    CourseCatalogueSidebarComponent,
    AsyncPipe,
    CourseCatalogueCardComponent,
    FrontendHeaderComponent,
    RouterLink,
    CategoryCardSkeletonComponent,
  ],
  providers: [
    {
      provide: APP_FILTER_SERVICE_CONFIG,
      useValue: {
        key: 'course-catalogue-filter',
        syncUrl: true,
        defaults: {
          sorts: [['camp.start', 'ASC']],
        },
      } as FilterServiceConfig,
    },
    FilterService,
  ],
  templateUrl: './course-catalogue.component.html',
})
export class CourseCatalogueComponent implements OnDestroy, AfterViewInit {
  protected readonly resourceService = inject(CourseService);
  protected readonly filterService = inject(FilterService);

  protected readonly dataSource = accumulatingDataSource(this.resourceService, {
    getPage: this.resourceService.getCourseCatalogue.bind(this.resourceService),
  });
  protected readonly firstTimeBusy$ = this.dataSource.busy$.pipe(
    map(() => false),
    // fixme: The first time busy of the dataSource is skipped
    skip(1),
    startWith(true),
  );

  @ViewChild('infiniteScrollAnchor', { static: false }) anchor!: ElementRef;
  @ViewChild('infiniteScrollContainer', { static: false })
  container!: ElementRef;
  private observer!: IntersectionObserver;

  public data: Signal<Course[]>;
  constructor() {
    this.data = toSignal(
      this.dataSource.connect(undefined as unknown as CollectionViewer),
      {
        initialValue: [],
      },
    );
    // Scroll back to top of page when filters change. In mobile view it's very
    // annoying that you're scrolled away from your filters while setting them,
    // so we check whether the view is scrolled past the filters.
    effect(() => {
      this.filterService.filters();
      if (
        document.documentElement.scrollTop >
        this.container?.nativeElement.offsetTop
      ) {
        document.documentElement.scrollTo({
          top: this.container?.nativeElement.offsetTop - 110,
        });
      }
    });
  }

  ngAfterViewInit() {
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // If the anchor becomes visible, load the next page
        if (entry.isIntersecting && this.dataSource.paginator.hasNextPage()) {
          this.dataSource.paginator.nextPage();
        }
      },
      {
        rootMargin: '100px', // Adjust to trigger earlier or later
      },
    );

    if (this.anchor?.nativeElement) {
      this.observer.observe(this.anchor.nativeElement);
    }
  }

  ngOnDestroy() {
    this.dataSource.disconnect();
    this.observer?.disconnect();
  }
}
